// ** React Imports
import { useCallback, useContext } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import queryString from "query-string";

// ** Custom Components
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import { CardFooter } from "components/layout/Card/CardFooter";
import LoadingContainer from "components/layout/LoadingContainer";
import ShowMoreLink from "components/layout/ShowMoreLink";
import Heading from "components/typography/HeadingLink";
import { StyledGridList } from "components/user/Browser/styles/GridList.styled";
import UserTileForUsersViewGrid from "components/user/UserTileForUsersViewGrid";
// ** Hooks
import useQuery from "hooks/useQuery";

// ** Utils & Helpers
import { searchSexCustomSort } from "utils/searchSexCustomSort";
import generatePath from "utils/generatePath";

// ** Routes
import containers from "containers";
import { initialQuery } from "containers/Users/initialQuery";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const Users = ({ users, loading, searchUrl }) => {
	const { t } = useTranslation(["common", "pages"]);

	const query = useQuery();

	const { isPhone, isTablet } = useContext(deviceTypeContext);

	const userSearchSex = useSelector((state) => state.globalUser.searchSex);
	const initialQueryString = queryString.stringify({
		...initialQuery,
		distance: query["distance"] ?? 50,
		sex: [...userSearchSex].sort(searchSexCustomSort).join("-"),
	});

	const to = generatePath(
		`${containers.users.link}/${searchUrl}?${initialQueryString}`
	);

	const renderUsersGrid = useCallback(() => {
		const finalUsers = isPhone || isTablet ? users : users.slice(0, 5);
		return finalUsers.map((item) => {
			return (
				<li key={item.id}>
					<UserTileForUsersViewGrid
						user={item}
						avatarProps={{
							borderSize: true,
						}}
					/>
				</li>
			);
		});
	}, [users, isPhone, isTablet]);

	// #####################################################

	return (
		<CardWrapper>
			<CardTitle>
				<Heading to={to} type="h3">
					{t("common:usersOnline")}
				</Heading>
			</CardTitle>

			<CardBody>
				<LoadingContainer
					empty={users.length === 0}
					loading={loading}
					loaderDefault
				>
					{!loading && (
						<StyledGridList inNearby>
							{renderUsersGrid()}
						</StyledGridList>
					)}
				</LoadingContainer>
			</CardBody>

			<CardFooter>
				<ShowMoreLink to={to} />
			</CardFooter>
		</CardWrapper>
	);
};

// #####################################################

export default Users;

// #####################################################
